import { css } from '@emotion/react';
import { AnchorLink } from 'gatsby-plugin-anchor-links';
import { getScreen } from '@/styles/media-query';

export type ButtonLinkProps = {
  className?: string;
  children?: React.ReactNode;
  to: string;
  variant:
    | 'header-demo'
    | 'header-download'
    | 'header-login'
    | 'contact-demo'
    | 'contact-download'
    | 'hero-demo'
    | 'hero-download'
    | 'top-more'
    | 'more'
    | 'plan-contact'
    | 'lp';
  isExternal?: boolean;
  isBlank?: boolean;
};

export const ButtonLink: React.FC<ButtonLinkProps> = ({
  className,
  children,
  to,
  variant,
  isExternal,
  isBlank,
}) => {
  return isExternal ? (
    <a
      css={linkStyle}
      className={className}
      href={to}
      target={isBlank ? '_blank' : undefined}
      rel={isBlank ? 'noreferrer' : undefined}
      data-variant={variant}
    >
      {children}
    </a>
  ) : (
    <AnchorLink
      css={linkStyle}
      className={className}
      to={to}
      gatsbyLinkProps={{ 'data-variant': variant }}
    >
      {children}
    </AnchorLink>
  );
};

const linkStyle = css`
  &[data-variant='header-demo'] {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent linear-gradient(105deg, #fe8321 0%, #ff9925 100%);
    border-radius: 9999px;
    box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.16);
    font-size: 1.4rem;
    color: #fff;
    font-weight: bold;
    letter-spacing: 0.06rem;
    min-height: 40px;
    transition: color 0.4s ease;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 9999px;
      opacity: 0;
      transition: opacity 0.4s ease;
    }

    &:hover {
      color: #fff;

      &::before {
        opacity: 1;
      }
    }
  }

  &[data-variant='header-download'] {
    border: 1.5px solid #fe8321;
    background: #fff;
    align-items: center;
    border-radius: 9999px;
    box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.16);
    display: flex;
    font-size: 1.4rem;
    color: #fe8321;
    font-weight: bold;
    justify-content: center;
    letter-spacing: 0.06rem;
    min-height: 40px;
    transition: background-color 0.4s ease, color 0.4s ease;

    @media ${getScreen('pc', '未満')} {
      font: normal normal bold 14px/20px 'Noto Sans JP', sans-serif;
      color: #fff;
      border: none;
      background: linear-gradient(270deg, #fe8321 0%, #ff9925 100%);
      padding: 13px 0 15px;
    }

    &:hover {
      background: #fe8321;
      color: #ffffff;
    }
  }

  &[data-variant='header-login'] {
    text-align: left;
    font: normal normal bold 13px/19px 'Noto Sans JP', sans-serif;
    letter-spacing: 0.1px;
    color: #252a40;
    padding: 8px;
  }

  &[data-variant='contact-demo'] {
    background: linear-gradient(106deg, #fe8321 0%, #ff9925 100%);
    box-shadow: 0px 1px 10px #00000029;
    border-radius: 9999px;
    padding: 11px 0 13px;
    color: #fff;
    font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
    letter-spacing: 0.96px;
    color: #ffffff;
    text-shadow: 0px 1px 2px #985f1e4d;
    position: relative;
    display: inline-block;
    transition: color 0.4s ease;

    @media ${getScreen('pc', '未満')} {
      padding: 13px 0 15px;
      display: block;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 9999px;
      opacity: 0;
      transition: opacity 0.4s ease;
    }

    &:hover {
      color: #fff;

      &::before {
        opacity: 1;
      }
    }
  }

  &[data-variant='contact-download'] {
    background: #ffffff;
    box-shadow: 0px 1px 10px #00000029;
    border: 1.5px solid #fe8321;
    border-radius: 9999px;
    padding: 9.5px 0 11.5px;
    font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
    letter-spacing: 0.96px;
    color: #fe8321;
    display: inline-block;
    transition: background-color 0.4s ease, color 0.4s ease;

    @media ${getScreen('pc', '未満')} {
      padding: 11.5px 0 13.5px;
      display: block;
    }

    &:hover {
      color: #fff;
      background: #ff9925;
    }
  }

  &[data-variant='hero-demo'] {
    line-height: 11px;
    background: transparent linear-gradient(106deg, #fe8321 0%, #ff9925 100%);
    border-radius: 9999px;
    color: #fff;
    font-size: 1.6rem;
    font-weight: bold;
    letter-spacing: 0.06em;
    text-shadow: 0px 1px 2px rgba(152, 95, 30, 0.3);
    display: flex;
    justify-content: center;
    align-items: center;
    width: 188px;
    height: 48px;
    position: relative;
    transition: color 0.4s ease;
    box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.16);

    @media ${getScreen('pc', '未満')} {
      height: 52px;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 9999px;
      opacity: 0;
      transition: opacity 0.4s ease;
    }

    &:hover {
      color: #fff;

      &::before {
        opacity: 1;
      }
    }
  }

  &[data-variant='hero-download'] {
    border-radius: 9999px;
    border: 1.5px solid #fe8321;
    background: #fff;
    color: #fe8321;
    font-size: 1.6rem;
    font-weight: bold;
    letter-spacing: 0.06em;
    line-height: 11px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 188px;
    height: 48px;
    transition: color 0.4s ease, background-color 0.4s ease;
    box-shadow: 0px 1px 10px rgba(0, 0, 0, 0.16);

    @media ${getScreen('pc', '未満')} {
      border: none;
      height: 52px;
      padding: 0;
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
    }

    &:hover {
      color: #fff;
      background: #fe8321;
    }
  }

  &[data-variant='top-more'] {
    display: block;
    text-align: center;
    font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
    letter-spacing: 0px;
    color: #00b3a7;
    background: #ffffff;
    box-shadow: 0px 0px 10px #00000029, 0 0 0 1px #00b3a7 inset;
    border-radius: 9999px;
    padding: 11px 0 13px;
    transition: color 0.4s ease, background-color 0.4s ease;

    &:hover {
      color: #fff;
      background: #00b3a7;
    }
  }

  &[data-variant='more'] {
    display: block;
    text-align: center;
    font: normal normal bold 16px/24px 'Noto Sans JP', sans-serif;
    letter-spacing: 0px;
    background: #00b3a7;
    color: #fff;
    box-shadow: 0 0 0 1px #00b3a7 inset;
    border-radius: 9999px;
    padding: 11px 0 13px;
    transition: color 0.4s ease, background-color 0.4s ease;

    &:hover {
      color: #00b3a7;
      background: #fff;
    }
  }

  &[data-variant='plan-contact'] {
    max-width: 292px;
    width: 100%;
    height: 48px;
    border: none;
    background: linear-gradient(101deg, #fe8321 0%, #ff9925 100%);
    box-shadow: 0px 1px 10px #00000029;
    border-radius: 9999px;
    font-weight: bold;
    font-size: 1.6rem;
    line-height: 1.1rem;
    letter-spacing: 0.96px;
    color: #fff;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 9999px;
      opacity: 0;
      transition: opacity 0.4s ease;
    }

    &:hover {
      color: #fff;

      &::before {
        opacity: 1;
      }
    }
  }

  &[data-variant='lp'] {
    display: block;
    text-align: center;
    background: linear-gradient(100deg, #fe8321 0%, #ff9925 100%);
    color: #ffffff;
    border-radius: 9999px;
    padding: 10px 0 14px;
    font: normal normal bold 21px/30px 'Noto Sans JP', sans-serif;
    letter-spacing: 1.26px;
    position: relative;
    text-shadow: 0px 1px 2px #985f1e4d;
    box-shadow: 0px 1px 10px #00000029;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.2);
      border-radius: 9999px;
      opacity: 0;
      transition: opacity 0.4s ease;
    }

    &:hover {
      color: #fff;

      &::before {
        opacity: 1;
      }
    }
  }
`;
